import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, TextField, Button, Box, Paper, Alert,
  Card, CardContent, CardActions, Grid, Chip, CircularProgress,
  InputAdornment, Avatar, Divider, Stack
} from '@mui/material';
import {
  Search, AutoAwesome, ArrowForward, LocationOn, Sell,
  Psychology, TipsAndUpdates, Bolt, ShoppingCart
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { aiAPI } from '../services/api';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const exampleQueries = [
  'Need bearing for CNC spindle, high RPM rated',
  'Hydraulic pump for injection molding machine',
  'Motor coupling for Toyota forklift',
  'Oil seals for air compressor',
  'Electrical contactor 3-phase 40A',
  'Stainless steel valve for food processing',
];

const AISearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) { toast.error('Enter a search query'); return; }
    setLoading(true);
    try {
      const response = await aiAPI.search({ query, n_results: 10 });
      setResults(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Please login to use AI search');
      } else {
        toast.error('Search failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter') handleSearch(); };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh' }}>
      {/* Hero Header */}
      <Box sx={{
        position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        py: { xs: 6, md: 8 }, textAlign: 'center',
      }}>
        {/* Animated orbs */}
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)' }} />
        <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.1) 0%, transparent 70%)' }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: 3, mx: 'auto', mb: 3,
              background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 12px 30px rgba(124,58,237,0.4)',
            }}>
              <Psychology sx={{ fontSize: 36, color: 'white' }} />
            </Box>
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 1.5, letterSpacing: '-0.02em' }}>
              AI-Powered Search
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8', maxWidth: 500, mx: 'auto', mb: 4, lineHeight: 1.7 }}>
              Describe what you need in plain language. Our RAG engine finds semantically 
              relevant parts — even without exact model numbers.
            </Typography>

            {/* Search Bar */}
            <Paper sx={{
              p: 0.8, display: 'flex', gap: 1, maxWidth: 650, mx: 'auto', borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }} elevation={0}>
              <TextField
                fullWidth variant="standard"
                placeholder="e.g., Need bearing for CNC spindle..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: <InputAdornment position="start" sx={{ ml: 1.5 }}><Search color="action" /></InputAdornment>,
                }}
                sx={{ '& input': { py: 1.8, fontSize: '1rem' } }}
              />
              <Button
                variant="contained" onClick={handleSearch}
                disabled={loading}
                sx={{ borderRadius: 2, px: 4, minWidth: 120 }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Search'}
              </Button>
            </Paper>
          </MotionBox>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Results */}
        {results && (
          <MotionBox initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {/* AI Explanation */}
            {results.explanation && (
              <Paper sx={{
                p: 3, mb: 3, borderRadius: 3,
                background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)',
                border: '1px solid #bfdbfe',
              }} elevation={0}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: '#dbeafe', color: '#2563eb', width: 40, height: 40 }}>
                    <TipsAndUpdates sx={{ fontSize: 22 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>AI Analysis</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {results.explanation}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Results Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Typography variant="h6" fontWeight={700}>
                {results.results?.length || 0} Results
              </Typography>
              <Chip
                icon={<AutoAwesome sx={{ fontSize: 14 }} />}
                label={results.method === 'semantic_rag' ? 'Semantic Match' : 'Keyword Match'}
                size="small" variant="outlined" color="secondary"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            {/* Result Cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {results.results?.map((item, i) => (
                <MotionCard
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  sx={{ borderRadius: 3, '&:hover': { borderColor: 'primary.light' } }}
                >
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{item.item_name}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip label={item.category} size="small" color="primary" variant="outlined" />
                        {item.condition && <Chip label={item.condition} size="small" variant="outlined" />}
                        {item.location && (
                          <Chip icon={<LocationOn sx={{ fontSize: 14 }} />} label={item.location} size="small" variant="outlined" />
                        )}
                      </Stack>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h5" color="primary.main" fontWeight={800}>
                        ₹{item.selling_price?.toLocaleString()}
                      </Typography>
                      {item.quantity && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: 'flex-end' }}>
                          <Sell sx={{ fontSize: 12 }} /> {item.quantity} available
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
                    <Button size="small" component={Link} to={`/item/${item.id}`} endIcon={<ArrowForward />}
                      sx={{ fontWeight: 600 }}>
                      View Details
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<ShoppingCart />}
                      component={Link} to={`/item/${item.id}`}>
                      Buy
                    </Button>
                  </CardActions>
                </MotionCard>
              ))}
            </Box>

            {results.results?.length === 0 && (
              <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }} elevation={0}>
                <Search sx={{ fontSize: 52, color: '#e2e8f0', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" fontWeight={600}>No matching items found</Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                  Try different terms or a broader description.
                </Typography>
              </Paper>
            )}
          </MotionBox>
        )}

        {/* Example Queries */}
        {!results && (
          <Paper sx={{ p: 4, borderRadius: 3, mt: 2 }} elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Avatar sx={{ bgcolor: '#f0f9ff', color: '#2563eb', width: 36, height: 36 }}>
                <Bolt sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>Try These Searches</Typography>
                <Typography variant="body2" color="text.secondary">Click to load into the search bar</Typography>
              </Box>
            </Box>
            <Grid container spacing={1.5}>
              {exampleQueries.map((example, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box
                    onClick={() => setQuery(example)}
                    sx={{
                      p: 2, borderRadius: 2, cursor: 'pointer',
                      border: '1px solid #f1f5f9',
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: '#f8fafc', borderColor: '#bfdbfe', transform: 'translateX(4px)' },
                    }}
                  >
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ArrowForward sx={{ fontSize: 14, color: 'primary.main' }} />
                      {example}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default AISearchPage;




